import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { useInventoryStore, cataloguePickerItems } from '../../../store/inventoryStore';
import { useWarehouseStore } from '../../../store/warehouseStore';
import { useStockMovementStore, AddStockRow, AddStockUnitIdent, generatePendingId } from '../../../store/stockMovementStore';
import { colours, StepNav, Select, QtyInput, FieldLabel, ItemCombobox } from './shared';
import { useOwnersStore } from '../../../store/ownersStore';
import { useProjectStore } from '../../../store/projectStore';

const StepIndicator: React.FC<{ steps: string[]; current: number }> = ({ steps, current }) => (
  <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '8px 16px 0', boxSizing: 'border-box', flexShrink: 0 }}>
    {steps.map((label, i) => {
      const done = i < current; const active = i === current;
      return (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:'50%', fontSize:12, fontWeight:700, backgroundColor: done ? '#16a34a' : active ? '#3b82f6' : '#374151', color:'#fff', flexShrink:0 }}>{done ? '✓' : i + 1}</span>
            <span style={{ fontSize:13, fontWeight:500, whiteSpace:'nowrap', color: active ? colours.text : done ? '#6b7280' : '#4b5563' }}>{label}</span>
          </div>
          {i < steps.length - 1 && <div style={{ flex:1, height:1, margin:'0 16px', backgroundColor: done ? '#16a34a' : colours.border }} />}
        </React.Fragment>
      );
    })}
  </div>
);

interface Props { onQueued: () => void; onOpenReview?: () => void; }

const STEPS = ['Select Items', 'Destination'];

const emptyUnit = (): AddStockUnitIdent => ({ serial: '', mac: '' });

const emptyRow = (): AddStockRow => ({
  itemId: '',
  quantity: 1,
  condition: 'new',
  units: [emptyUnit()],
  reference: '',
  owner: '',
});

function resizeUnits(current: AddStockUnitIdent[], qty: number): AddStockUnitIdent[] {
  const n = Math.max(1, qty);
  return Array.from({ length: n }, (_, i) => current[i] ?? emptyUnit());
}

function rowIdentsComplete(row: AddStockRow, requiresSerial: boolean, requiresMac: boolean): boolean {
  if (!requiresSerial && !requiresMac) return true;
  const qty = Math.max(1, row.quantity);
  for (let i = 0; i < qty; i++) {
    const unit = row.units[i] ?? emptyUnit();
    if (requiresSerial && !unit.serial.trim()) return false;
    if (requiresMac && !unit.mac.trim()) return false;
  }
  return true;
}

type IdentField = 'serial' | 'mac';
type IdentFocus = { rowIdx: number; unitIdx: number; field: IdentField };

function identFields(requiresSerial: boolean, requiresMac: boolean): IdentField[] {
  const fields: IdentField[] = [];
  if (requiresSerial) fields.push('serial');
  if (requiresMac) fields.push('mac');
  return fields;
}

function identKey(rowIdx: number, unitIdx: number, field: IdentField): string {
  return `${rowIdx}:${unitIdx}:${field}`;
}

function nextIdentSlot(
  row: AddStockRow,
  requiresSerial: boolean,
  requiresMac: boolean,
  fromUnit: number,
  fromField: IdentField,
): { unitIdx: number; field: IdentField } | 'grow' | null {
  const fields = identFields(requiresSerial, requiresMac);
  if (fields.length === 0) return null;
  const qty = Math.max(1, row.quantity);
  let unitIdx = fromUnit;
  let fi = fields.indexOf(fromField) + 1;
  while (unitIdx < qty) {
    while (fi < fields.length) {
      const field = fields[fi];
      if (!(row.units[unitIdx]?.[field] ?? '').trim()) {
        return { unitIdx, field };
      }
      fi++;
    }
    unitIdx++;
    fi = 0;
  }
  return 'grow';
}

const AddStockForm: React.FC<Props> = ({ onQueued, onOpenReview }) => {
  const { items, fetchItems } = useInventoryStore();
  const { warehouses, storageAreasByWarehouse, defaultWarehouseId } = useWarehouseStore();
  const { addPending } = useStockMovementStore();
  const { owners: ownerList, fetchOwners } = useOwnersStore();
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';

  const [step, setStep]     = useState(0);
  const [rows, setRows]     = useState<AddStockRow[]>([emptyRow()]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [identFocus, setIdentFocus] = useState<IdentFocus | null>(null);

  // Destination
  const defaultWh = warehouses.find(w => w.id === defaultWarehouseId) ?? warehouses[0];
  const [warehouseId, setWarehouseId]   = useState(defaultWh?.id ?? '');
  const [storageAreaId, setStorageAreaId] = useState<string>('');

  useEffect(() => { fetchItems(); fetchOwners(); }, [fetchItems, fetchOwners]);

  // Keep warehouseId in sync if warehouses load after mount
  useEffect(() => {
    if (!warehouseId && defaultWh) setWarehouseId(defaultWh.id);
  }, [defaultWh, warehouseId]);

  const areas = useMemo(() =>
    warehouseId ? (storageAreasByWarehouse[warehouseId] ?? []) : [],
    [storageAreasByWarehouse, warehouseId]
  );

  // Auto-select first storage area when warehouse changes
  useEffect(() => {
    if (areas.length > 0 && !areas.find(a => a.id === storageAreaId)) {
      setStorageAreaId(areas[0].id);
    }
  }, [areas, storageAreaId]);

  // ── Row helpers ────────────────────────────────────────────────────────

  const updateRow = (idx: number, patch: Partial<AddStockRow>) => {
    setRows(prev => {
      const next = [...prev];
      const current = next[idx];
      next[idx] = { ...current, ...patch };
      if (patch.quantity !== undefined) {
        next[idx].units = resizeUnits(current.units, patch.quantity);
      }
      if (patch.itemId !== undefined && patch.itemId !== current.itemId) {
        next[idx].units = resizeUnits(next[idx].units, next[idx].quantity).map(() => emptyUnit());
      }
      return next;
    });
  };

  const updateUnit = (idx: number, unitIdx: number, field: keyof AddStockUnitIdent, value: string) => {
    setRows(prev => {
      const next = [...prev];
      const units = resizeUnits(next[idx].units, next[idx].quantity);
      units[unitIdx] = { ...units[unitIdx], [field]: value };
      next[idx] = { ...next[idx], units };
      return next;
    });
  };

  const removeRow = (idx: number) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
    setExpanded(prev => {
      const next = new Set<number>();
      prev.forEach(i => {
        if (i < idx) next.add(i);
        else if (i > idx) next.add(i - 1);
      });
      return next;
    });
  };

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const toggleExpand = (idx: number) =>
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(idx) ? s.delete(idx) : s.add(idx);
      return s;
    });

  const selectItem = (idx: number, itemId: string) => {
    updateRow(idx, { itemId });
    const item = items.find(it => it.id === itemId);
    const fields = identFields(!!item?.requiresSerial, !!item?.requiresMac);
    setExpanded(prev => {
      const s = new Set(prev);
      if (fields.length > 0) s.add(idx);
      else s.delete(idx);
      return s;
    });
    if (fields.length > 0) setIdentFocus({ rowIdx: idx, unitIdx: 0, field: fields[0] });
    else setIdentFocus(null);
  };

  const applyIdentAndAdvance = (rowIdx: number, unitIdx: number, field: IdentField, value: string) => {
    const trimmed = value.trim();
    const row = rows[rowIdx];
    if (!row) return;
    const item = items.find(it => it.id === row.itemId);
    const requiresSerial = !!item?.requiresSerial;
    const requiresMac = !!item?.requiresMac;
    const patchedUnits = resizeUnits(row.units, row.quantity);
    patchedUnits[unitIdx] = { ...patchedUnits[unitIdx], [field]: trimmed };
    const patchedRow: AddStockRow = { ...row, units: patchedUnits };
    if (!trimmed) {
      setRows(prev => {
        const next = [...prev];
        next[rowIdx] = patchedRow;
        return next;
      });
      return;
    }
    const slot = nextIdentSlot(patchedRow, requiresSerial, requiresMac, unitIdx, field);
    const fields = identFields(requiresSerial, requiresMac);

    if (slot === 'grow' && trimmed && fields.length > 0) {
      const quantity = patchedRow.quantity + 1;
      setRows(prev => {
        const next = [...prev];
        next[rowIdx] = { ...patchedRow, quantity, units: resizeUnits(patchedUnits, quantity) };
        return next;
      });
      setIdentFocus({ rowIdx, unitIdx: quantity - 1, field: fields[0] });
      return;
    }

    setRows(prev => {
      const next = [...prev];
      next[rowIdx] = patchedRow;
      return next;
    });
    if (slot && slot !== 'grow') {
      setIdentFocus({ rowIdx, unitIdx: slot.unitIdx, field: slot.field });
    }
  };

  useLayoutEffect(() => {
    if (!identFocus || step !== 0) return;
    const key = identKey(identFocus.rowIdx, identFocus.unitIdx, identFocus.field);
    const focusEl = () => {
      const el = document.querySelector(`[data-add-ident="${key}"]`) as HTMLInputElement | null;
      if (!el) return false;
      el.focus();
      return true;
    };
    if (focusEl()) return;
    const id = window.setTimeout(focusEl, 0);
    return () => window.clearTimeout(id);
  }, [identFocus, step, expanded]);

  // ── Validation ─────────────────────────────────────────────────────────

  const step1Valid = rows.length > 0 && rows.every(r => {
    if (!r.itemId) return false;
    const item = items.find(it => it.id === r.itemId);
    return rowIdentsComplete(r, !!item?.requiresSerial, !!item?.requiresMac);
  });
  const step2Valid = warehouseId !== '';

  // ── Queue ──────────────────────────────────────────────────────────────

  const handleQueue = () => {
    addPending({
      type: 'add_stock',
      id: generatePendingId(),
      rows,
      warehouseId,
      storageAreaId: storageAreaId || null,
    });
    setRows([emptyRow()]);
    setExpanded(new Set());
    setStep(0);
    onQueued();
  };

  const { border, cardBg, headerBg, muted, text, inputBg, rowHover } = colours;
  const fieldInput =
    'w-full text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 box-border';
  const identInput =
    'text-sm px-2 py-1.5 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500 box-border';

  return (
    <div className="flex-1 flex flex-col min-h-0" style={{ backgroundColor: colours.pageBg }}>
      <StepIndicator steps={STEPS} current={step} />

      {/* ── Step 1: Select Items ── */}
      {step === 0 && (
        <div className="flex-1 overflow-auto px-4 py-4">
          <div className="rounded overflow-hidden" style={{ border: `1px solid ${border}` }}>
            <table className="w-full border-collapse">
              <thead style={{ backgroundColor: headerBg }}>
                <tr>
                  {['', 'Item Type', 'Qty', 'Condition', 'Reference', 'Owner', ''].map((h, i) => (
                    <th
                      key={i}
                      className="px-0.5 pt-1.5 pb-1 text-left text-[11px] uppercase tracking-wide font-medium"
                      style={{ color: muted, width: i === 0 ? 22 : i === 2 ? 58 : i === 6 ? 28 : undefined }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ backgroundColor: cardBg }}>
                {rows.map((row, idx) => {
                  const item = items.find(it => it.id === row.itemId);
                  const needsSerial = !!item?.requiresSerial;
                  const needsMac = !!item?.requiresMac;
                  const showIdents = !!row.itemId && (needsSerial || needsMac);
                  const isExp = expanded.has(idx);
                  const identIncomplete = showIdents && !rowIdentsComplete(row, needsSerial, needsMac);
                  const qty = Math.max(1, row.quantity);
                  let identFilled = 0;
                  let identRequired = 0;
                  if (showIdents) {
                    for (let i = 0; i < qty; i++) {
                      const unit = row.units[i] ?? emptyUnit();
                      if (needsSerial) {
                        identRequired++;
                        if (unit.serial.trim()) identFilled++;
                      }
                      if (needsMac) {
                        identRequired++;
                        if (unit.mac.trim()) identFilled++;
                      }
                    }
                  }
                  const identLabel = needsSerial && needsMac
                    ? 'Serial & MAC'
                    : needsSerial
                      ? 'Serial'
                      : 'MAC';
                  return (
                    <React.Fragment key={idx}>
                      <tr>
                        <td className="px-0.5 py-0 align-middle">
                          {showIdents ? (
                            <button
                              type="button"
                              onClick={() => toggleExpand(idx)}
                              className="inline-flex items-center justify-center w-[22px] h-[22px] rounded transition-colors"
                              style={{ color: identIncomplete ? '#d97706' : muted }}
                              title={isExp ? 'Hide serial / MAC' : identIncomplete ? `Enter ${identLabel.toLowerCase()} (${identFilled}/${identRequired})` : `Show ${identLabel.toLowerCase()}`}
                            >
                              {isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          ) : null}
                        </td>
                        <td className="px-0.5 py-0 align-middle" style={{ minWidth: 200 }}>
                          <ItemCombobox
                            value={row.itemId}
                            items={cataloguePickerItems(items, row.itemId)}
                            onChange={itemId => selectItem(idx, itemId)}
                          />
                        </td>
                        <td className="px-0.5 py-0 align-middle">
                          <QtyInput
                            isDark={isDark}
                            value={row.quantity}
                            onChange={e => updateRow(idx, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                          />
                        </td>
                        <td className="px-0.5 py-0 align-middle">
                          <select
                            value={row.condition}
                            onChange={e => updateRow(idx, { condition: e.target.value as 'new' | 'used' })}
                            className={fieldInput}
                            style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                          >
                            <option value="new">New</option>
                            <option value="used">Used</option>
                          </select>
                        </td>
                        <td className="px-0.5 py-0 align-middle" style={{ minWidth: 120 }}>
                          <input
                            type="text"
                            placeholder="PO / delivery ref…"
                            value={row.reference}
                            onChange={e => updateRow(idx, { reference: e.target.value })}
                            className={fieldInput}
                            style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                          />
                        </td>
                        <td className="px-0.5 py-0 align-middle" style={{ minWidth: 150 }}>
                          <select
                            value={row.owner}
                            onChange={e => updateRow(idx, { owner: e.target.value })}
                            className={fieldInput}
                            style={{ backgroundColor: inputBg, borderColor: border, color: row.owner ? text : muted }}
                          >
                            <option value="">Select owner…</option>
                            {ownerList.map(o => <option key={o.id} value={o.name}>{o.name}</option>)}
                          </select>
                        </td>
                        <td className="px-0.5 py-0 align-middle">
                          {rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRow(idx)}
                              className="p-0.5 rounded transition-colors hover:bg-red-900/30"
                              style={{ color: muted }}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </td>
                      </tr>

                      {showIdents && isExp && (
                        <tr>
                          <td />
                          <td colSpan={6} className="px-0.5 pb-0.5 pt-0.5">
                            <div className="flex flex-col" style={{ gap: 2 }}>
                              {resizeUnits(row.units, row.quantity).map((unit, si) => (
                                <div key={si} className="flex items-center" style={{ gap: 4 }}>
                                  <span className="text-[11px] w-3.5 flex-shrink-0" style={{ color: muted }}>{si + 1}</span>
                                  {needsSerial && (
                                    <input
                                      type="text"
                                      placeholder="Serial"
                                      value={unit.serial}
                                      required
                                      autoComplete="off"
                                      spellCheck={false}
                                      data-add-ident={identKey(idx, si, 'serial')}
                                      onChange={e => updateUnit(idx, si, 'serial', e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key !== 'Enter') return;
                                        e.preventDefault();
                                        applyIdentAndAdvance(idx, si, 'serial', e.currentTarget.value);
                                      }}
                                      className={identInput}
                                      style={{
                                        backgroundColor: inputBg,
                                        borderColor: unit.serial.trim() ? border : '#d97706',
                                        color: text,
                                        width: 220,
                                      }}
                                    />
                                  )}
                                  {needsMac && (
                                    <input
                                      type="text"
                                      placeholder="MAC"
                                      value={unit.mac}
                                      required
                                      autoComplete="off"
                                      spellCheck={false}
                                      data-add-ident={identKey(idx, si, 'mac')}
                                      onChange={e => updateUnit(idx, si, 'mac', e.target.value)}
                                      onKeyDown={e => {
                                        if (e.key !== 'Enter') return;
                                        e.preventDefault();
                                        applyIdentAndAdvance(idx, si, 'mac', e.currentTarget.value);
                                      }}
                                      className={identInput}
                                      style={{
                                        backgroundColor: inputBg,
                                        borderColor: unit.mac.trim() ? border : '#d97706',
                                        color: text,
                                        width: 220,
                                      }}
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Add Row */}
                <tr>
                  <td colSpan={7} className="px-0.5 py-1">
                    <button
                      type="button"
                      onClick={addRow}
                      className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1 rounded transition-colors"
                      style={{ color: '#3b82f6' }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = rowHover)}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <Plus size={12} />
                      Add row
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Step 2: Destination ── */}
      {step === 1 && (
        <div className="flex-1 overflow-auto px-4 py-4">
          <div
            className="max-w-md rounded p-5 flex flex-col gap-4"
            style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}
          >
            <div>
              <p className="text-sm mb-4" style={{ color: muted }}>
                Choose the warehouse and storage area where these items will be stored.
              </p>
            </div>
            <Select
              label="Warehouse"
              value={warehouseId}
              onChange={e => { setWarehouseId(e.target.value); setStorageAreaId(''); }}
              wFull
            >
              <option value="">Select warehouse…</option>
              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </Select>
            <Select
              label="Storage Area"
              value={storageAreaId}
              onChange={e => setStorageAreaId(e.target.value)}
              wFull
              disabled={areas.length === 0}
            >
              {areas.length === 0
                ? <option value="">No storage areas</option>
                : areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)
              }
            </Select>

            {/* Summary */}
            <div className="pt-2" style={{ borderTop: `1px solid ${border}` }}>
              <FieldLabel>Summary</FieldLabel>
              <div className="mt-2 flex flex-col gap-1">
                {rows.filter(r => r.itemId).map((row, i) => {
                  const item = items.find(it => it.id === row.itemId);
                  return (
                    <div key={i} className="text-sm" style={{ color: text }}>
                      <span className="font-medium">{item?.name ?? '—'}</span>
                      <span style={{ color: muted }}> × {row.quantity}  ({row.condition})</span>
                      {row.reference && <span style={{ color: muted }}> · {row.reference}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <StepNav
        onBack={step > 0 ? () => setStep(s => s - 1) : undefined}
        onNext={step === 0 ? () => setStep(1) : undefined}
        onQueue={step === 1 ? handleQueue : undefined}
        onReview={onOpenReview}
        nextDisabled={!step1Valid}
        queueDisabled={!step2Valid}
        queueLabel="Add to Queue ✓"
      />
    </div>
  );
};

export default AddStockForm;
